import { Link } from "react-router-dom";
import { Heart, Ear, ShieldCheck, Sun, BadgeCheck, ArrowRight } from "lucide-react";
import { Reveal, MaskedLine, FadeIn } from "@/components/Reveal";
import { IMAGES } from "@/lib/images";

const VALUES = [
  {
    icon: Heart,
    title: "Dignity",
    text: "Every senior is a whole person with a lifetime of stories. We honour who they are, not just the care they need.",
    testid: "value-card-dignity",
  },
  {
    icon: Ear,
    title: "Active Listening",
    text: "The best companionship starts with truly hearing someone — their memories, worries, jokes and quiet moments.",
    testid: "value-card-listening",
  },
  {
    icon: ShieldCheck,
    title: "Safety",
    text: "Background-checked, reference-checked and insured companions you can trust in your loved one's home.",
    testid: "value-card-safety",
  },
  {
    icon: Sun,
    title: "Warmth",
    text: "We hire for kindness first. Skills can be taught — genuine warmth for people cannot.",
    testid: "value-card-warmth",
  },
];

const GUARANTEES = [
  "Criminal background checks on every companion",
  "Personal & professional reference verification",
  "Personality and interest-based matching",
  "The same companion on every visit",
  "Warm family updates after each visit",
  "Fully insured for your peace of mind",
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="mx-auto max-w-7xl px-4 pb-16 pt-16 sm:px-6 lg:px-8 lg:pt-24">
        <p className="eyebrow mb-5" data-testid="about-eyebrow">Our Story</p>
        <h1 className="max-w-3xl font-serif text-4xl font-bold leading-[1.1] tracking-tight text-espresso sm:text-5xl lg:text-6xl" data-testid="about-heading">
          <MaskedLine delay={0.1}>We started Off Our Rockers</MaskedLine>
          <MaskedLine delay={0.25}>
            because <em className="text-brand">loneliness</em> isn't
          </MaskedLine>
          <MaskedLine delay={0.4}>part of the plan.</MaskedLine>
        </h1>
      </section>

      {/* Story */}
      <section className="mx-auto grid max-w-7xl items-center gap-14 px-4 pb-20 sm:px-6 lg:grid-cols-2 lg:pb-28 lg:px-8">
        <Reveal>
          <div className="overflow-hidden rounded-[2.5rem] border-8 border-white shadow-2xl shadow-espresso/15">
            <img
              src={IMAGES.aboutWalk}
              alt="Two companions on a mountain path overlooking the Rockies"
              className="aspect-[4/5] w-full object-cover"
              data-testid="about-story-image"
            />
          </div>
        </Reveal>
        <Reveal delay={0.15} className="space-y-5 text-lg leading-relaxed text-warmgray">
          <p>
            Off Our Rockers Seniors Care was born in Calgary from a simple observation: too many
            wonderful seniors were spending their days alone — not because family didn't care, but
            because life, distance and work get in the way.
          </p>
          <p>
            We're a <strong className="text-espresso">non-medical</strong> companionship company. We
            don't do bathing or medication — we do conversation, laughter, walks along the Bow,
            crossword puzzles, trips to the farmer's market, and the kind of company that makes a
            house feel like a home again.
          </p>
          <p>
            Whether your loved one lives at home, in assisted living, or in a care facility, we
            work with you to understand their individual needs — and match them with the perfect
            companion.
          </p>
        </Reveal>
      </section>

      {/* Values */}
      <section className="bg-sand">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <Reveal className="mb-14 max-w-2xl">
            <p className="eyebrow mb-4">What Guides Us</p>
            <h2 className="font-serif text-3xl font-semibold tracking-tight text-espresso sm:text-4xl lg:text-5xl">
              Four values, one promise
            </h2>
          </Reveal>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.1}>
                <article
                  data-testid={v.testid}
                  className="h-full rounded-3xl border border-espresso/10 bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-espresso/10"
                >
                  <div className="mb-5 inline-flex rounded-2xl bg-brand-soft p-4">
                    <v.icon className="h-8 w-8 text-brand" />
                  </div>
                  <h3 className="mb-3 font-serif text-xl font-medium text-espresso sm:text-2xl">{v.title}</h3>
                  <p className="text-base leading-relaxed text-warmgray">{v.text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Guarantee */}
      <section className="mx-auto grid max-w-7xl items-center gap-14 px-4 py-20 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-8 lg:py-28">
        <Reveal>
          <p className="eyebrow mb-4">Our Quality Guarantee</p>
          <h2 className="mb-5 font-serif text-3xl font-semibold tracking-tight text-espresso sm:text-4xl lg:text-5xl">
            Companions we'd trust with our own parents
          </h2>
          <p className="text-lg leading-relaxed text-warmgray">
            Every Off Our Rockers companion earns their place through a careful, human process —
            because "nice on paper" isn't enough when it's your mum or dad.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <ul className="grid gap-4 sm:grid-cols-2" data-testid="guarantee-list">
            {GUARANTEES.map((g) => (
              <li key={g} className="flex items-start gap-3 rounded-2xl border border-espresso/10 bg-white p-5 shadow-sm">
                <BadgeCheck className="mt-0.5 h-6 w-6 shrink-0 text-sage" />
                <span className="text-base font-medium leading-snug text-espresso">{g}</span>
              </li>
            ))}
          </ul>
        </Reveal>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
        <FadeIn>
          <div className="rounded-[2.5rem] bg-espresso px-6 py-16 text-center sm:px-12 lg:py-20" data-testid="about-cta-banner">
            <h2 className="mx-auto mb-5 max-w-2xl font-serif text-3xl font-semibold tracking-tight text-cream sm:text-4xl">
              Ready to meet the people behind the name?
            </h2>
            <p className="mx-auto mb-9 max-w-xl text-lg text-cream/75">
              Book a free consultation and let's talk about your loved one — no obligation, ever.
            </p>
            <Link to="/contact" data-testid="about-cta-button" className="btn-primary !bg-gold !text-espresso hover:!bg-brand hover:!text-white">
              Book a Free Consultation <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </FadeIn>
      </section>
    </div>
  );
}
